import { type AudiobookChapter, buildPath } from "@contract";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { errorCodeOf } from "../../../api/fetcher";
import { AudioProvider } from "../../../components/audio/AudioProvider";
import { Card } from "../../../components/data/Card";
import { DataTable } from "../../../components/data/DataTable";
import { Banner } from "../../../components/feedback/Banner";
import { EmptyState } from "../../../components/feedback/EmptyState";
import { ErrorBanner } from "../../../components/feedback/ErrorBanner";
import { ErrorState } from "../../../components/feedback/ErrorState";
import { Skeleton } from "../../../components/feedback/Skeleton";
import { Bytes } from "../../../components/format/Bytes";
import { DateTime } from "../../../components/format/DateTime";
import { Duration } from "../../../components/format/Duration";
import { Num } from "../../../components/format/Num";
import { WorkspaceActions } from "../../../components/layout/WorkspaceActions";
import { ChapterListen } from "../audio/ChapterListen";
import { useBookDetail } from "../bookContext";
import { useAudiobook } from "../render/useRender";

export function AudiobookScreen() {
  const book = useBookDetail();
  return (
    <AudioProvider key={book.id}>
      <AudiobookContent />
    </AudioProvider>
  );
}
function AudiobookContent() {
  const { t } = useTranslation(["audiobook", "render", "common"]);
  const book = useBookDetail();
  const query = useAudiobook();
  const data = query.data;
  if (!data && query.isPending) return <Skeleton variant="text" lines={8} />;
  if (!data && errorCodeOf(query.error) === "NOT_FOUND")
    return (
      <EmptyState
        title={t("audiobook:notReady")}
        action={
          <Link className="text-accent underline" to={`/books/${book.id}/render/progress`}>
            {t("audiobook:viewProgress")}
          </Link>
        }
      >
        {t("audiobook:notReadyBody")}
      </EmptyState>
    );
  if (!data)
    return (
      <ErrorState
        code={errorCodeOf(query.error)}
        onRetry={() => {
          void query.refetch();
        }}
      />
    );
  return (
    <div className="flex flex-col gap-4" data-screen="audiobook">
      <ErrorBanner error={query.error} />
      {data.stale ? <Banner tone="warn">{t("audiobook:stale")}</Banner> : null}
      {data.assembling ? (
        <Banner
          tone="working"
          action={
            <Link className="text-accent underline" to={`/books/${book.id}/render/progress`}>
              {t("audiobook:viewProgress")}
            </Link>
          }
        >
          {t("audiobook:assembling")}
        </Banner>
      ) : null}
      <div className="grid min-w-0 gap-6 xl:grid-cols-[minmax(0,1fr)_300px]">
        <Card
          label={t("audiobook:label")}
          title={t("audiobook:title")}
          titleAs="h1"
          padding="none"
          className="min-w-0"
        >
          <div className="flex flex-wrap gap-6 px-6 py-5">
            {data.coverPresent ? (
              <img
                src={`/api${buildPath("/books/:id/cover", { id: book.id })}`}
                alt={t("audiobook:coverAlt", { title: data.title })}
                className="h-48 w-36 shrink-0 border border-line object-cover"
              />
            ) : (
              <div className="flex h-48 w-36 shrink-0 items-center border border-line bg-sunken p-4 font-serif text-title-sm">
                {data.title}
              </div>
            )}
            <div className="flex min-w-0 flex-1 flex-col gap-3">
              <h3 className="font-serif text-title font-semibold">{data.title}</h3>
              {data.authorName ? <p className="text-ink-2">{data.authorName}</p> : null}
              <p>
                {data.narratorName
                  ? t("audiobook:narrator", { name: data.narratorName })
                  : t("audiobook:narratorUnknown")}
              </p>
              <p className="font-mono text-label text-ink-2">
                <Duration ms={data.durationMs} style="words" /> ·{" "}
                {t(`common:bookLanguage.${data.language}`)} ·{" "}
                {t("audiobook:chapterCount", { count: data.chapters.length })}
              </p>
              <p className="text-ink-2">
                {t("audiobook:assembled")} <DateTime value={data.createdAt} style="dateTime" />
              </p>
              <WorkspaceActions>
                <a
                  className="inline-flex min-h-control items-center gap-3 rounded-sm border border-ink bg-ink px-4 py-2 font-medium text-surface"
                  href={`/api${buildPath("/books/:id/audiobook/m4b", { id: book.id })}`}
                  download
                >
                  {t("audiobook:downloadM4b")}
                  <span className="font-mono text-label">
                    <Bytes value={data.m4b.sizeBytes} />
                  </span>
                </a>
                <a
                  className="inline-flex min-h-control items-center gap-3 rounded-sm border border-line px-4 py-2"
                  href={`/api${buildPath("/books/:id/audiobook/zip", { id: book.id })}`}
                  download
                >
                  {t("audiobook:downloadZip")}
                  <span className="font-mono text-label">
                    <Bytes value={data.zip.sizeBytes} />
                  </span>
                </a>
              </WorkspaceActions>
            </div>
          </div>
          <div className="overflow-x-auto">
            <div className="min-w-[670px]">
              <DataTable<AudiobookChapter>
                rows={data.chapters}
                rowKey={(row) => row.chapterId}
                template="36px minmax(160px,1fr) 80px 80px 105px 110px"
                columns={[
                  {
                    id: "number",
                    header: t("render:number"),
                    cell: (row) => <Num value={row.number} />,
                  },
                  {
                    id: "title",
                    header: t("render:chapter"),
                    className: "whitespace-normal",
                    cell: (row) => (
                      <div>
                        <Link
                          className="hover:text-accent"
                          to={`/books/${book.id}/review/${row.chapterId}`}
                        >
                          {row.title}
                        </Link>
                        {row.stale ? <p className="text-warn">{t("render:stale")}</p> : null}
                      </div>
                    ),
                  },
                  {
                    id: "duration",
                    header: t("audiobook:duration"),
                    cell: (row) => <Duration ms={row.durationMs} />,
                  },
                  {
                    id: "start",
                    header: t("audiobook:start"),
                    cell: (row) => <Duration ms={row.startMs} />,
                  },
                  {
                    id: "listen",
                    header: t("render:audio"),
                    cell: (row) => (
                      <ChapterListen
                        bookId={book.id}
                        chapterId={row.chapterId}
                        version={row.renderedAt}
                      />
                    ),
                  },
                  {
                    id: "render",
                    header: t("render:action"),
                    cell: () => (
                      <Link className="text-accent" to={`/books/${book.id}/render`}>
                        {t("render:renderAgain")}
                      </Link>
                    ),
                  },
                ]}
              />
            </div>
          </div>
        </Card>
        <aside className="flex flex-col gap-4">
          <Card label={t("audiobook:formats")}>
            <h3 className="font-medium">{t("audiobook:m4bTitle")}</h3>
            <p className="mt-2 leading-relaxed text-ink-2">{t("audiobook:m4bBody")}</p>
            <h3 className="mt-5 font-medium">{t("audiobook:zipTitle")}</h3>
            <p className="mt-2 leading-relaxed text-ink-2">{t("audiobook:zipBody")}</p>
          </Card>
          <Card label={t("audiobook:changes")}>
            <p className="leading-relaxed text-ink-2">{t("audiobook:changesBody")}</p>
            <Link className="mt-4 text-accent" to={`/books/${book.id}/render`}>
              {t("audiobook:reviewEstimate")}
            </Link>
          </Card>
        </aside>
      </div>
    </div>
  );
}
